import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthLayout } from './AuthLayout.jsx';
import { Button, Field, Input, PasswordInput, ErrorNotice } from '../../components/ui/index.js';
import { useAuth } from '../../lib/auth.jsx';
import { useMutation } from '../../hooks/useApi.js';
import { http } from '../../lib/api.js';

/**
 * Accepting a team invitation.
 *
 * The token in the link is the credential — there is no account yet to
 * authenticate against. It is single-use and expires in seven days, so a link
 * forwarded to the wrong person stops working rather than remaining a standing
 * login into somebody's payment account.
 *
 * They choose their own password and sign in immediately, because an invitation
 * that ends at "now go and log in" is one a busy cashier abandons.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const token = params.get('token') ?? '';
  const [form, setForm] = useState({ name: '', password: '' });

  const { mutate, loading, error } = useMutation(async () => {
    const member = await http.post('/auth/accept-invite', {
      token,
      password: form.password,
      ...(form.name.trim() ? { name: form.name.trim() } : {}),
    });

    // Straight in. They have just proved who they are and chosen a password;
    // asking them to type it again is ceremony.
    await signIn({ email: member.email, password: form.password });
    navigate('/dashboard', { replace: true });
  });

  if (!token) {
    return (
      <AuthLayout
        title="That link is incomplete"
        subtitle="The invitation link is missing its token. Ask whoever invited you to send it again."
        footer={<Link to="/login" className="font-medium text-accent-ink hover:underline">Back to sign in</Link>}
      >
        <span />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set your password"
      subtitle="You have been invited to a DistrxPay account. Choose a password and you are in."
      footer={
        <>Already have an account? <Link to="/login" className="font-medium text-accent-ink hover:underline">Sign in</Link></>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}
        noValidate
      >
        {error && <ErrorNotice error={error} title="Could not accept this invitation" />}

        <Field label="Your name" hint="What your colleagues will see beside the sales you take.">
          {(props) => (
            <Input
              {...props}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ravi Iyer"
              autoFocus
            />
          )}
        </Field>

        <Field
          label="Password"
          required
          hint="At least 10 characters. Nobody else will ever see it — not even whoever invited you."
          error={error?.fieldErrors?.password}
        >
          {(props) => (
            <PasswordInput
              {...props}
              name="password"
              autoComplete="new-password"
              value={form.password}
              invalid={Boolean(error?.fieldErrors?.password)}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          )}
        </Field>

        <Button type="submit" size="lg" loading={loading} className="w-full">
          Set password and sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

export default AcceptInvitePage;
